import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useModifierSurveyContext } from '../../pages/Modifier'
import { useModifierSurveyStore } from '../../store/useModifierSurveryStore'
import { HeatingKind } from '../../utils/enums/modifierSurveyEnums'
import { Button } from '../core/Button'
import { QuestionWidget } from './QuestionWidget'

export interface HeatingFormType {
  heatingKwh: number
}

export interface SurveyQuestionHeatingProps {
  editable: boolean
}

export const SurveyQuestionHeating = ({
  editable,
}: SurveyQuestionHeatingProps) => {
  const context = useModifierSurveyContext()
  const { t } = useTranslation(['modifierSurvey'])
  const options = Object.keys(HeatingKind).map((value: string) => ({
    name: t(`modifierSurvey:heating.${value}`),
    value,
  }))

  const [selectedValue, setSelectedValue] = useState<HeatingKind | undefined>(
    undefined
  )

  const heatingKind = useModifierSurveyStore().modifierResults?.heatingKind
  const heatingKindText =
    useModifierSurveyStore().modifierResults?.heatingKindText
  const setHeatingKind = useModifierSurveyStore().setHeatingKind
  const setHeatingKindText = useModifierSurveyStore().setHeatingKindText
  const heatingKwh = useModifierSurveyStore(
    (state) => state.modifierResults?.heatingKwh
  )
  const setHeatingKwh = useModifierSurveyStore((state) => state.setHeatingKwh)

  useEffect(() => {
    setSelectedValue(heatingKind)
  }, [heatingKind])

  const onSelect = (newOption: string) => {
    setHeatingKind(newOption as HeatingKind)
  }

  const onCommentInput = (comment: string) => {
    setHeatingKindText(comment)
  }

  const { register } = useForm<HeatingFormType>({
    mode: 'onChange',
    defaultValues: {
      heatingKwh,
    },
  })

  return (
    <>
      <div
        className={classNames(editable ? 'pt-8 w-full px-8 py-16' : 'w-full')}
      >
        <QuestionWidget
          options={options}
          selectedValue={selectedValue}
          onSelect={onSelect}
          onCommentInput={onCommentInput}
          note={heatingKindText}
          editable={editable}
        />

        <form className="mt-4 w-7/12 pr-4">
          <label
            htmlFor="electricityShare"
            className="block text-sm font-medium text-gray-700"
          >
            {t('modifierSurvey:heating.heatingKwhLabel')}
          </label>
          <div className="relative mt-1 rounded-md shadow-sm">
            <input
              id="heatingKwh"
              {...register('heatingKwh')}
              type="number"
              className="block w-full rounded-md border-gray-300 pr-12 focus:border-brand focus:ring-brand sm:text-sm"
              placeholder="0"
              onBlur={(e) => setHeatingKwh(Number.parseInt(e.target.value))}
              disabled={!editable}
            />
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <span className="text-gray-500 sm:text-sm" id="price-currency">
                kWh
              </span>
            </div>
          </div>
        </form>
      </div>

      {editable && (
        <div className="bg-gray-100 py-4 flex items-center justify-between w-full px-8">
          <Button
            onClick={() => context?.onPrevTab()}
            label={t('modifierSurvey:common.backBtn')}
            variant="secondary"
          />
          <Button
            onClick={() => context?.onNextTab()}
            label={t('modifierSurvey:common.nextBtn')}
            variant="primary"
            disabled={selectedValue === undefined}
          />
        </div>
      )}
    </>
  )
}
